import { Box, Grid, Skeleton, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { selectUserType } from "../../../../../../modules/login/loginSelectors"
import { UserType } from "../../../../../../modules/login/loginTypes"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"

import { TakeawayProductCardSkeleton } from "./TakeawayProductCardSkeleton"

const SKELETON_GROUPS: readonly number[] = [4, 3]

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    heroWrap: {
        position: "relative",
        flexShrink: 0,
    },
    hero: {
        width: "100%",
        height: "25rem",
        transform: "none",
        borderRadius: 0,
        background: theme.palette.tailwind.slate[300],
    },
    heroOverlay: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: "1.5rem",
        display: "flex",
        alignItems: "center",
        gap: "1.5rem",
    },
    heroTitle: {
        transform: "none",
        borderRadius: ".5rem",
    },
    heroBtn: {
        width: "2.5rem",
        height: "2.5rem",
        transform: "none",
        borderRadius: ".75rem",
        flexShrink: 0,
    },
    dateBar: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: ".75rem 1.5rem",
        background: theme.palette.common.white,
        flexShrink: 0,
    },
    daysGroup: {
        display: "flex",
        alignItems: "center",
        gap: ".125rem",
        background: theme.palette.tailwind.blue[100],
        borderRadius: ".75rem",
        flexShrink: 0,
    },
    dayPill: {
        transform: "none",
        borderRadius: ".75rem",
    },
    orText: {
        transform: "none",
        borderRadius: ".375rem",
        flexShrink: 0,
    },
    dateButton: {
        transform: "none",
        borderRadius: ".75rem",
        flexShrink: 0,
    },
    categoryBar: {
        display: "flex",
        alignItems: "center",
        background: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        flexShrink: 0,
    },
    categoryTabs: {
        flex: 1,
        display: "flex",
        alignItems: "center",
    },
    categoryTab: {
        transform: "none",
        borderRadius: ".375rem",
        // matches MuiTab padding: "1rem 1.25rem"
        margin: "1rem 1.25rem",
    },
    categoryActions: {
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
        paddingRight: ".75rem",
        flexShrink: 0,
    },
    categoryActionIcon: {
        transform: "none",
        borderRadius: ".375rem",
    },
    content: {
        padding: "1.5rem",
        flex: 1,
        background: theme.palette.tailwind.slate[100],
    },
    groupTitle: {
        transform: "none",
        borderRadius: ".375rem",
    },
}))

export function TakeawayFormSkeleton() {
    const { classes } = useStyles()
    const userType = useAppSelector(selectUserType)
    const isOnBehalfOf = userType !== UserType.USER

    return (
        <Box className={classes.root}>
            {/* Hero */}
            <Box className={classes.heroWrap}>
                <Skeleton variant="rectangular" animation={false} className={classes.hero} />
                <Box className={classes.heroOverlay}>
                    <Skeleton
                        variant="rounded"
                        animation={false}
                        width="12rem"
                        height="2.5rem"
                        className={classes.heroTitle}
                    />
                    <Skeleton variant="rounded" animation={false} className={classes.heroBtn} />
                </Box>
            </Box>

            {/* Date bar */}
            <Box className={classes.dateBar}>
                <Box className={classes.daysGroup}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            animation="wave"
                            width="3.75rem"
                            height="3.5rem"
                            className={classes.dayPill}
                        />
                    ))}
                </Box>
                <Skeleton variant="rounded" animation="wave" width="1.5rem" height="1rem" className={classes.orText} />
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    width="8rem"
                    height="2.75rem"
                    className={classes.dateButton}
                />
            </Box>

            {/* Category bar */}
            <Box className={classes.categoryBar}>
                <Box className={classes.categoryTabs}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            animation="wave"
                            width="5rem"
                            height="1.125rem"
                            className={classes.categoryTab}
                        />
                    ))}
                </Box>
                <Box className={classes.categoryActions}>
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="1.5rem"
                        height="1.5rem"
                        className={classes.categoryActionIcon}
                    />
                    {!isOnBehalfOf && (
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            width="1.5rem"
                            height="1.5rem"
                            className={classes.categoryActionIcon}
                        />
                    )}
                </Box>
            </Box>

            {/* Product groups */}
            <Stack gap="2rem" className={classes.content}>
                {SKELETON_GROUPS.map((count, groupIndex) => (
                    <Stack gap="1.5rem" key={groupIndex}>
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            width="10rem"
                            height="1.5rem"
                            className={classes.groupTitle}
                        />
                        <Grid container gap="1.5rem" alignItems="stretch">
                            {Array.from({ length: count }).map((_, cardIndex) => (
                                <TakeawayProductCardSkeleton key={cardIndex} />
                            ))}
                        </Grid>
                    </Stack>
                ))}
            </Stack>
        </Box>
    )
}
